import type { ReactNode } from 'react';

import type {
  ExifMetadataCategory,
  ImageMetadataReport,
} from '../domain/models/image';
import styles from './ImageMetadataNotice.module.css';

const CATEGORY_LABELS: Record<ExifMetadataCategory, string> = {
  location: 'GPS location',
  device: 'Camera or device details',
  'capture-time': 'Capture date and time',
  'camera-settings': 'Camera settings',
  orientation: 'Image orientation',
  thumbnail: 'Embedded thumbnail',
  description: 'Description or author details',
  other: 'Other EXIF data',
};

export function ImageMetadataNotice({
  fileName,
  metadata,
}: {
  fileName: string;
  metadata: ImageMetadataReport;
}): ReactNode {
  return (
    <section
      className={styles.notice}
      role="status"
      aria-label="Image privacy check"
    >
      <div className={styles.heading}>
        <span className={styles.mark} aria-hidden="true">
          {metadata.found ? '!' : '✓'}
        </span>
        <div>
          <span className={styles.eyebrow}>Image privacy check</span>
          <strong className={styles.title}>
            {metadata.found ? 'Metadata found' : 'No EXIF metadata found'}
          </strong>
        </div>
      </div>

      {metadata.found ? (
        <>
          <p className={styles.summary}>
            <strong>{fileName}</strong> contains:
          </p>
          <ul className={styles.categories}>
            {metadata.categories.map((category) => (
              <li key={category}>{CATEGORY_LABELS[category]}</li>
            ))}
          </ul>
          <p className={styles.outcome}>
            These details will be removed before upload. The image itself stays
            in the preview.
          </p>
        </>
      ) : (
        <p className={styles.outcome}>
          Nothing will be removed from <strong>{fileName}</strong>.
        </p>
      )}
    </section>
  );
}
